import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash, Share2, Copy, QrCode } from 'lucide-react';
import { useApps } from '../hooks/useApps';
import { QRCodeCanvas } from 'qrcode.react';

const AppList = ({ supabase }) => {
  const { apps, loading, error, deleteApp, duplicateApp } = useApps(supabase);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);

  const handleDelete = async (id, appName) => {
    if (window.confirm(`Are you sure you want to delete the app "${appName}"? This will not delete the scenarios.`)) {
      try {
        await deleteApp(id);
      } catch (error) {
        console.error('Error deleting app:', error);
        alert('Failed to delete app. Please try again.');
      }
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await duplicateApp(id);
    } catch (error) {
      console.error('Error duplicating app:', error);
      alert('Failed to duplicate app. Please try again.');
    }
  };

  const handleShare = (app) => {
    setSelectedApp(app);
    setShowShareModal(true);
  };

  const handleCopyToClipboard = () => {
    const shareUrl = `https://pwa.qr2air.com?token=${selectedApp.token}`;
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        console.log('URL copied to clipboard');
        // Optional: Show a toast notification
      })
      .catch(err => {
        console.error('Failed to copy URL: ', err);
      });
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById('qr-code-canvas');
    if (canvas) {
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${selectedApp.name.replace(/\s+/g, '-')}-qr-code.png`;
      downloadLink.click();
    }
  };

  return (
    <div className="bg-gray-50">
      <main className="px-0">
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Apps</h2>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">
              Create PWA wrappers that bundle multiple scenarios
            </p>
            <Link
              to="/apps/new"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create App
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10">
            <p className="text-gray-500">Loading apps...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  Error loading apps: {error}
                </p>
              </div>
            </div>
          </div>
        ) : apps.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500 mb-2">No apps found. Create your first one!</p>
            <p className="text-sm text-gray-400">
              Apps allow you to bundle multiple scenarios into a single installable PWA
            </p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    App Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Token
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scenarios
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {apps.map((app) => (
                  <tr key={app.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{app.name}</div>
                      {app.description && (
                        <div className="text-sm text-gray-500">{app.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{app.token}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {app.scenario_count || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleShare(app)}
                        className="text-green-600 hover:text-green-900 mr-4"
                        title="Share app"
                      >
                        <Share2 className="h-4 w-4 inline" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(app.id)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                        title="Duplicate app"
                      >
                        <Copy className="h-4 w-4 inline" />
                      </button>
                      <Link
                        to={`/apps/${app.id}`}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                        title="Edit app"
                      >
                        <Edit className="h-4 w-4 inline" />
                      </Link>
                      <button
                        onClick={() => handleDelete(app.id, app.name)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete app"
                      >
                        <Trash className="h-4 w-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Share Modal */}
        {showShareModal && selectedApp && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Share App: {selectedApp.name}</h3>

              {/* QR Code */}
              <div className="flex justify-center mb-4">
                <QRCodeCanvas
                  id="qr-code-canvas"
                  value={`https://pwa.qr2air.com?token=${selectedApp.token}`}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>

              {/* URL */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Share URL
                </label>
                <input
                  type="text"
                  readOnly
                  value={`https://pwa.qr2air.com?token=${selectedApp.token}`}
                  className="w-full px-3 py-2 border rounded-md"
                  onClick={(e) => e.target.select()}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowShareModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Close
                </button>
                <button
                  onClick={downloadQRCode}
                  className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  <QrCode className="h-4 w-4 mr-2" />
                  Download QR
                </button>
                <button
                  onClick={handleCopyToClipboard}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy URL
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AppList;
